import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { generateOptiAds } from '../../../../front/src/components/Ads/PageListAds';
import { CreateTopicButton } from '../../../../front/src/components/Button/CreateTopicButton';
import { LoaderBlock } from '../../../../front/src/components/LoaderBlock';
import { confirmationToast } from '../../../../front/src/components/Toast';
import { useIsMobile } from '../../../../front/src/hooks/useIsMobile';
import { useOnAuthChange } from '../../../../front/src/hooks/useOnAuthChange';
import { useAppSelector } from '../../../../front/src/redux/hooks';
import {
  selectCustomDensity,
  selectIsBot,
  selectIsSignedIn,
} from '../../../../front/src/redux/slices/utils-slice';
import { homeTopicsQuery } from '../../../../front/src/thread/api/topic';
import ContentHeader from '../../../../front/src/thread/Topic/ContentHeader';
import { TopicList } from '../../../../front/src/thread/Topic/TopicList';
import type { Topic } from '../../../../front/src/types/api';
import type { LastTopicsBlockData, SortType } from '../../../../front/src/types/layout';
import { throttle } from '../../../../front/src/utils/common-utils';
import type { DensityType } from '../../../../front/src/utils/density';
import { useHasAds } from '../../utils/common';
import { LinkToAll } from '../Home/children/LinkToAll';

const messages = defineMessages({
  recentTopics: { defaultMessage: `Latest topics`, id: 'xdiu4Q' },
  allTopics: { defaultMessage: 'See all topics', id: 'wlELin' },
  updatedMessage: { defaultMessage: 'The list is up to date', id: 'CNpo38' },
});

interface BlockLastTopicsProps {
  withLinkToAllCTA?: boolean;
  className?: string;
  block: LastTopicsBlockData;
  isResponsive?: boolean;
  isSidebar?: boolean;
  itemTitleClassName?: string;
}

const hasTopicsListBeenUpdated = (currentTopics: Topic[] = [], newTopics: Topic[]) => {
  const isSameListLength = currentTopics.length === newTopics.length;

  const isListModified = currentTopics.some(
    (topic, tIndex) =>
      topic.id !== newTopics[tIndex].id ||
      topic.content !== newTopics[tIndex].content ||
      topic.lastMessage?.id !== newTopics[tIndex].lastMessage?.id,
  );

  return !isSameListLength || isListModified;
};

export const BlockLastTopics: FC<BlockLastTopicsProps> = memo(function BlockLastTopics(props) {
  const {
    withLinkToAllCTA,
    className,
    block,
    isResponsive = true,
    isSidebar = false,
    itemTitleClassName = '',
  } = props;
  const { options, data: blockData } = block;

  const isMobile = useIsMobile();
  const defaultDensity = (isMobile ? 'compact' : 'standard') as DensityType;

  const {
    sort: blockDataSort,
    limit: blockDataLimit = 20,
    customTitle,
    density: blockDataDensity = defaultDensity,
  } = options;
  const limit = +blockDataLimit; // parseInt to handle string number value from BO

  const [topics, setTopics] = useState<Topic[]>(blockData);
  const [isCheckingForToken, setIsCheckingForToken] = useState(true);
  const [isRefreshingData, setIsRefreshingData] = useState(false);

  const topicsRef = useRef(blockData);
  const isFirstRender = useRef(true);

  const isBot = useAppSelector(selectIsBot);
  const isSignedIn = useAppSelector(selectIsSignedIn);

  const customDensity = useAppSelector(selectCustomDensity) || blockDataDensity;
  const densityToDisplay = isSidebar ? blockDataDensity : customDensity;

  const client = useApolloClient();
  const { query } = useRouter();

  const querySort = useMemo(
    () => (isSidebar ? 'newest' : (query?.sort as SortType)),
    [isSidebar, query?.sort],
  );

  const fetchClientTopicsAndUpdateState = useCallback(
    async (fetchOptions?: { isUserRefresh: boolean }) => {
      console.log('🔄 Fetching topics');
      const isUserRefresh = !!fetchOptions?.isUserRefresh;

      try {
        isUserRefresh && setIsRefreshingData(true);

        const shouldSkipContent = densityToDisplay !== 'card';

        const { data } = await client.query({
          query: homeTopicsQuery,
          variables: { sort: querySort || blockDataSort, limit, skipContent: shouldSkipContent },
          fetchPolicy: 'network-only',
        });

        isUserRefresh && setIsRefreshingData(false);
        const requestTopics = data.website.topicList.topics;
        const topicsHaveBeenUpdated = hasTopicsListBeenUpdated(topicsRef.current, requestTopics);

        if (!topicsHaveBeenUpdated) {
          isUserRefresh &&
            confirmationToast(<FormattedMessage {...messages.updatedMessage} />, {
              autoClose: 1000,
            });

          return;
        }

        setTopics(requestTopics);
        isUserRefresh &&
          confirmationToast(<FormattedMessage {...messages.updatedMessage} />, {
            autoClose: 1000,
          });
        console.log('ℹ️ Topics list refreshed');
      } catch (e) {
        isUserRefresh && setIsRefreshingData(false);
        console.error('❌ Error while fetching topics', e);
      }
    },
    [blockDataSort, client, densityToDisplay, limit, querySort],
  );

  useEffect(() => {
    // Avoid keeping topics state in fetchClientTopicsAndUpdateState, that results in refetching twice each time topics are updated
    topicsRef.current = topics;
  }, [topics]);

  const forceRefreshByUser = useMemo(
    () => throttle(() => fetchClientTopicsAndUpdateState({ isUserRefresh: true }), 5000),
    [fetchClientTopicsAndUpdateState],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      if (isBot) {
        setIsCheckingForToken(false);
        isFirstRender.current = false;
        return;
      }

      // If user is authenticated on first render (e.g. browser refresh), fetch their topics
      if (isSignedIn) {
        fetchClientTopicsAndUpdateState();
      }

      setIsCheckingForToken(false);
      isFirstRender.current = false;
    }
  }, [fetchClientTopicsAndUpdateState, isBot, isSignedIn]);

  // Listen for home refresh event (triggered when clicking logo on home page)
  useEffect(() => {
    if (isBot || !isSignedIn) {
      return;
    }

    const handleHomeRefresh = () => {
      // Refetch topics with user-specific data after navigation.refresh()
      fetchClientTopicsAndUpdateState();
    };

    window.addEventListener('homeRefreshComplete', handleHomeRefresh);

    return () => {
      window.removeEventListener('homeRefreshComplete', handleHomeRefresh);
    };
  }, [fetchClientTopicsAndUpdateState, isBot, isSignedIn]);

  const onAuthChangeRefresh = useCallback(() => {
    if (!isBot) {
      // Refetch in all cases to get fresh data
      // Login: with userReport and other user-specific data
      // Logout: without user data but with up-to-date topics
      fetchClientTopicsAndUpdateState();
    }
    setIsCheckingForToken(false);
  }, [fetchClientTopicsAndUpdateState, isBot]);

  // When the user logs in or out, refetch the topics (e.g. for pending moderation, userReport)
  useOnAuthChange(onAuthChangeRefresh);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // Do not fetch on first render (handled in first useEffect)
    }
    fetchClientTopicsAndUpdateState();
  }, [querySort, fetchClientTopicsAndUpdateState]);

  // Render the first list straight from `blockData` (fetched server-side by
  // insertLayoutData → fetchLastTopicsAPOLLO) so the topic links (/t/<slug>) are
  // present in the SSR HTML for crawlers/AI bots (B8_08, SEO recirculation).
  // The loader is only shown while checking the token AND when there is nothing
  // to display yet — a populated `blockData` renders immediately in SSR. The
  // client refetch (useEffect) still refreshes with user-specific data after
  // hydration. SSR data is user-neutral (getStaticProps is unauthenticated).
  if (isCheckingForToken && !topics?.length) {
    return <LoaderBlock />;
  }

  return (
    <div className={twMerge('BlockLastTopics flex flex-col gap-y-4 md:gap-y-6', className)}>
      <ContentHeader
        withCategoriesMenu
        isResponsive={isResponsive}
        contentTitle={customTitle || <FormattedMessage {...messages.recentTopics} />}
        titleClassName={block.locate === 'right' ? 'text-md font-roboto font-semibold' : ''}
        listLength={(!isCheckingForToken && topics?.length) || undefined}
        forceRefetch={{
          cb: forceRefreshByUser,
          isRefreshing: isRefreshingData,
        }}
        {...(!isSidebar && {
          topicListActions: {
            sortMenu: { sortType: querySort || blockDataSort },
            densityMenu: { densityType: densityToDisplay, listType: 'lastTopics' },
            createTopicCTA: true,
          },
        })}
      />

      {!!topics?.length ? (
        <BlockLastTopicsLoaded
          isResponsive={isResponsive}
          itemTitleClassName={itemTitleClassName}
          isSidebar={isSidebar}
          topics={topics}
          limit={limit}
          isRefreshingData={isRefreshingData}
          density={densityToDisplay}
        />
      ) : null}

      {withLinkToAllCTA && (
        <LinkToAll to="topics" params={{ filter: 'new' }} intlMessage={messages.allTopics} />
      )}
      {isSidebar && <CreateTopicButton className="mx-auto" />}
    </div>
  );
});

interface BlockLastTopicsLoadedProps
  extends Required<
    Pick<BlockLastTopicsProps, 'isResponsive' | 'isSidebar' | 'itemTitleClassName'>
  > {
  topics: Topic[];
  limit: number;
  density: DensityType;
  isRefreshingData: boolean;
}

const BlockLastTopicsLoaded: FC<BlockLastTopicsLoadedProps> = memo(props => {
  const { limit } = props;

  const hasAds = useHasAds();

  const computedAds = useMemo(
    () =>
      !hasAds
        ? undefined
        : [
            ...generateOptiAds({
              start: 2,
              freq: 7,
              isMobile: false,
              total: limit,
            }),
            ...generateOptiAds({
              start: 2,
              freq: 5,
              isMobile: true,
              total: limit,
            }),
          ],
    [hasAds, limit],
  );

  return <TopicList {...props} computedAds={computedAds} />;
});
